import { notifyOnError } from '~/rage/decorators';
import {
	BankOperationType,
	BankTransactionCategory,
	BankTransactionReason,
	IBankPinAttempt,
	IBankStats,
	IBankTransaction as IBankTransactionBackend,
	ICharacterClientBankData,
	Job,
	WalletType,
	bankTransactionReasonTexts,
} from '~/shared';
import { IObservable, Subject } from '../../utils';
import { BackendService } from '../BackendService';
import { GameConfigService } from '../GameConfigService';
import { INotificationsService } from '../NotificationsService';
import { IPlayerService } from '../PlayerService';
import { RpcService } from '../RpcService';
import { IBankService, IBankTransaction } from './bankServiceTypes';

export class BankService implements IBankService {
	private readonly transactions = new Subject<IBankTransaction[]>([]);
	private readonly stats = new Subject<IBankStats>({
		earned: 0,
		spent: 0,
		registeredAt: Date.now(),
		cardExpiresAt: Date.now(),
	});
	private readonly banExpiresAt = new Subject<number | null>(null);
	private readonly isBankAppAvailable = new Subject<boolean>(false);
	private readonly pinCodeLength = new Subject<number>(0);
	private readonly cardNumber = new Subject<string>('');
	private readonly isPinCodeSet = new Subject<boolean>(false);
	private readonly pinCodeResetResendAt = new Subject<number | null>(null);
	private readonly operationTypesFees: {
		[key in BankOperationType]: Subject<number>;
	} = {
		[BankOperationType.DEPOSIT]: new Subject(1),
		[BankOperationType.WITHDRAW]: new Subject(1),
		[BankOperationType.TRANSFER]: new Subject(1),
		[BankOperationType.TAX]: new Subject(1),
	};

	private readonly wallets: {
		[key in WalletType]: Subject<number>;
	};

	private readonly walletDisplayedDiffs: {
		[key in WalletType]: Subject<number>;
	};

	private readonly walletPendingDiffs: {
		[key in WalletType]: number;
	};

	private readonly walletPrevValues: {
		[key in WalletType]: number;
	};

	private readonly walletInitialized: {
		[key in WalletType]: boolean;
	};

	constructor(
		private readonly backend: BackendService,
		private readonly gameConfig: GameConfigService,
		private readonly notifications: INotificationsService,
		private readonly rpc: RpcService,
		private readonly player: IPlayerService,
	) {
		this.wallets = {
			[WalletType.Cash]: new Subject(0),
			[WalletType.Bank]: new Subject(0),
			[WalletType.Donate]: new Subject(0),
		};

		this.walletDisplayedDiffs = {
			[WalletType.Cash]: new Subject(0),
			[WalletType.Bank]: new Subject(0),
			[WalletType.Donate]: new Subject(0),
		};

		this.walletPendingDiffs = {
			[WalletType.Cash]: 0,
			[WalletType.Bank]: 0,
			[WalletType.Donate]: 0,
		};

		this.walletPrevValues = {
			[WalletType.Cash]: 0,
			[WalletType.Bank]: 0,
			[WalletType.Donate]: 0,
		};

		this.walletInitialized = {
			[WalletType.Cash]: false,
			[WalletType.Bank]: false,
			[WalletType.Donate]: false,
		};

		for (const walletType of Object.values(WalletType)) {
			this.wallets[walletType].subscribe((newValue) => {
				const prevValue = this.walletPrevValues[walletType];
				const diff = newValue - prevValue;

				if (diff === 0) {
					return;
				}

				if (!this.walletInitialized[walletType]) {
					this.walletPrevValues[walletType] = newValue;
					this.walletInitialized[walletType] = true;
					return;
				}

				const currentDisplayed =
					this.walletDisplayedDiffs[walletType].getValue();
				if (currentDisplayed !== 0) {
					this.walletPendingDiffs[walletType] += diff;
				} else {
					this.walletDisplayedDiffs[walletType].next(currentDisplayed + diff);
				}

				this.walletPrevValues[walletType] = newValue;
			});
		}

		this.gameConfig.getConfigs().subscribe((config) => {
			if (!config || !config.bank) {
				return;
			}

			this.pinCodeLength.next(config.bank.pinCodeLength);

			for (const operationType of Object.values(BankOperationType)) {
				this.operationTypesFees[operationType].next(
					config.bank.operationFeesByType[operationType].transferFee,
				);
			}
		});

		this.backend.addEventHandler('bank:set', (data) => {
			const { wallet, amount } = data as { wallet: WalletType; amount: number };
			this.wallets[wallet].next(amount);
		});

		this.backend.addEventHandler(
			'bank:transaction',
			(data: IBankTransactionBackend) => {
				this.transactions.next([
					this.getTransactionFromData(data),
					...this.transactions.getValue(),
				]);
			},
		);

		this.backend.addEventHandler(
			'bank:data',
			(clientData: ICharacterClientBankData) => {
				this.cardNumber.next(clientData.cardNumber);
				this.isPinCodeSet.next(clientData.isPinSet);
				this.isBankAppAvailable.next(clientData.isPinSet);
			},
		);

		this.player.waitForCharacterSelect().then(() => {
			this.resetWalletInitialization();
			this.fetch();
		});
	}

	public async fetch(): Promise<void> {
		await Promise.all([this.fetchTransactions(), this.fetchBankStats()]);
	}

	public getBalance(wallet: WalletType): IObservable<number> {
		return this.wallets[wallet];
	}

	public getCardNumber(): IObservable<string> {
		return this.cardNumber;
	}

	@notifyOnError
	public async exchange(
		fromWallet: WalletType,
		toWallet: WalletType,
		amount: number,
	): Promise<void> {
		if (amount <= 0) {
			this.notifications.showError('Сумма должна быть больше нуля');
			return;
		}

		await this.backend.invoke('bank:exchange', {
			fromWallet,
			toWallet,
			amount,
		});
	}

	@notifyOnError
	public async transfer(cardNumber: string, amount: number): Promise<void> {
		if (amount <= 0) {
			this.notifications.showError('Сумма должна быть больше нуля');
			return;
		}

		await this.backend.invoke('bank:transfer', {
			cardNumber,
			amount,
		});
	}

	public getTransactionHistory(): IObservable<IBankTransaction[]> {
		return this.transactions;
	}

	public async close(): Promise<void> {
		await this.rpc.invokeClient('bank:hide');
		await this.backend.invoke('bank:invalidatePinCode');
	}

	public getTransferFee(type: BankOperationType): IObservable<number> {
		return this.operationTypesFees[type];
	}

	public getBankStats(): IObservable<IBankStats> {
		return this.stats;
	}

	private async fetchBankStats(): Promise<void> {
		const stats = await this.backend.invoke<IBankStats>(
			'bank:getMonthlyMoneyFlow',
		);
		this.stats.next(stats);
	}

	private async fetchTransactions(): Promise<void> {
		const { transactions } = await this.backend.invoke<{
			transactions: IBankTransactionBackend[];
		}>('bank:getTransactions');

		this.transactions.next(
			transactions
				.map((item) => this.getTransactionFromData(item))
				.sort((a, b) => {
					return b.createdAt.getTime() - a.createdAt.getTime();
				}),
		);
	}

	private getTransactionFromData(
		data: IBankTransactionBackend,
	): IBankTransaction {
		return {
			...data,
			createdAt: new Date(data.createdAt),
			description:
				bankTransactionReasonTexts[data.reason as BankTransactionReason] ??
				'Банковская транзакция',
			category: BankTransactionCategory.BANK,
		};
	}

	private async fetchClientData(): Promise<void> {
		await this.backend.invoke<void>('bank:fetchClientData');
	}

	public getIsPinCodeSet(): IObservable<boolean> {
		return this.isPinCodeSet;
	}

	public async setPinCode(pinCode: string): Promise<void> {
		await this.backend.invoke('bank:setPinCode', { pinCode });
		await this.fetchClientData();
	}

	public async checkPinCode(pinCode: string): Promise<IBankPinAttempt> {
		const attempt = await this.backend.invoke<IBankPinAttempt>(
			'bank:validatePinCode',
			{
				pinCode,
			},
		);

		this.banExpiresAt.next(attempt.banExpiresAt ?? null);

		return attempt;
	}

	public getBanExpiresAt(): IObservable<number | null> {
		return this.banExpiresAt;
	}

	public getIsBankAppAvailable(): IObservable<boolean> {
		return this.isBankAppAvailable;
	}

	public getPinCodeLength(): IObservable<number> {
		return this.pinCodeLength;
	}

	public async getCardOwner(cardNumber: string): Promise<string | null> {
		return this.backend.invoke<string>('bank:getCardOwner', {
			cardNumber,
		});
	}

	@notifyOnError
	public async getDailyJobProfit(jobId: Job): Promise<number> {
		return this.backend.invoke<number>('bank:getDailyJobProfit', {
			jobId,
		});
	}

	public getBalanceDiff(wallet: WalletType): IObservable<number> {
		return this.walletDisplayedDiffs[wallet];
	}

	public commitBalanceDiff(wallet: WalletType): number {
		return this.walletDisplayedDiffs[wallet].getValue();
	}

	public finishDisplayingBalanceDiff(wallet: WalletType): void {
		this.walletDisplayedDiffs[wallet].next(0);

		const pending = this.walletPendingDiffs[wallet];
		if (pending !== 0) {
			this.walletPendingDiffs[wallet] = 0;
			this.walletDisplayedDiffs[wallet].next(pending);
		}
	}

	@notifyOnError
	public async requestPinCodeReset(): Promise<{ resendAvailableAt: number }> {
		const result = await this.backend.invoke<{ resendAvailableAt: number }>(
			'bank:requestPinCodeReset',
		);
		this.pinCodeResetResendAt.next(result.resendAvailableAt);
		return result;
	}

	public async confirmPinCodeResetCode(
		code: string,
	): Promise<{ token: string }> {
		return this.backend.invoke<{ token: string }>(
			'bank:confirmPinCodeResetCode',
			{ code },
		);
	}

	@notifyOnError
	public async resetPinCode(token: string, newPinCode: string): Promise<void> {
		await this.backend.invoke('bank:resetPinCode', {
			token,
			pinCode: newPinCode,
		});
		this.pinCodeResetResendAt.next(null);
		await this.fetchClientData();
	}

	public getPinCodeResetResendAt(): IObservable<number | null> {
		return this.pinCodeResetResendAt;
	}

	private resetWalletInitialization(): void {
		for (const walletType of Object.values(WalletType)) {
			this.walletInitialized[walletType] = false;
			this.walletPrevValues[walletType] = 0;
			this.walletDisplayedDiffs[walletType].next(0);
			this.walletPendingDiffs[walletType] = 0;
		}
	}
}
